// 直した版 f (2026-10-15・開発版 ?parrySfx=f だけ・配布版は変えない)。e を土台に「当たった物の材質」で鳴らし分ける。
//   ユーザー原文 (10-15・音のパック 1014b Q-C2)「重くするってよりかは材質に合わせて音を変える感じだと思うんだけど」
//   e = 冷たい銀 (ジャスト) の層 + 技の重さで低く大きくなる重さの層。f はその上で
//     ・鋼の素材の速さ・大きさ、鐘・尾・高域の削りを材質ごとに変える (柄・衝撃波は鋼をほぼ鳴らさない)
//     ・材質だけの層を1つ足す (柄 = 木の鈍い当たり、鉄 = 合わない低い鳴りと鎖の粒 等)
//     ・重さは残すが幅を狭める (材質の差のほうを大きく聞かせる)
//   脳汁のスナップ (3kHz より上の 6ms と 520Hz の 14ms) はどの材質でも残す。
//   記録 = docs/research/2026-10-15-parry-material-and-exec-sfx.md
import type { ParryMaterial } from './parry_material'

/** 材質だけの層 (audio.ts の playParryMaterialLayer が合成で鳴らす)。 */
export type ParryMaterialLayer = 'none' | 'wood' | 'ironRing' | 'silk' | 'air' | 'gold' | 'broad' | 'tip'

export type ParryMaterialVoice = {
  /** 鋼の素材の再生速度に掛ける (小さいほど低く大きな得物)。 */
  steelRate: number
  /** 鋼の素材の大きさに掛ける (0 = 鋼の鳴りなし)。 */
  steelGain: number
  /** 5200Hz より上の削り (dB)。 */
  shelfDb: number
  /** 3040Hz の鐘・尾・ジャストの澄んだ鐘に掛ける。 */
  bellMul: number
  tailMul: number
  /** 衝撃の芯 (220Hz・380Hz の雑音・沈む音・72Hz) に掛ける。 */
  coreMul: number
  /** e の重さの層に掛ける。 */
  weightMul: number
  layer: ParryMaterialLayer
  /** 材質の層の大きさ。 */
  layerGain: number
}

export const PARRY_MATERIAL_VOICE: Record<ParryMaterial, ParryMaterialVoice> = {
  // 刀・細い剣 = e の冷たい銀そのもの (基準)
  blade:      { steelRate: 1.00, steelGain: 1.00, shelfDb: -6,  bellMul: 1.00, tailMul: 1.00, coreMul: 1.00, weightMul: 0.6, layer: 'none',     layerGain: 0 },
  // 幅広の黒い刃 = 鋼を低く遅く・低い長めの鋼の鳴りを足す
  greatblade: { steelRate: 0.82, steelGain: 1.10, shelfDb: -9,  bellMul: 0.55, tailMul: 0.6,  coreMul: 1.15, weightMul: 1.0, layer: 'broad',    layerGain: 1.0 },
  // 槍の穂先 = 細く高い先の鳴り・芯は少し軽い
  lance:      { steelRate: 1.06, steelGain: 0.85, shelfDb: -5,  bellMul: 0.8,  tailMul: 0.8,  coreMul: 0.9,  weightMul: 0.6, layer: 'tip',      layerGain: 1.0 },
  // 柄・石突き・蹴り = 鋼は鳴らさず木と体の鈍い当たり
  haft:       { steelRate: 0.80, steelGain: 0.15, shelfDb: -14, bellMul: 0,    tailMul: 0,    coreMul: 1.1,  weightMul: 0.8, layer: 'wood',     layerGain: 1.0 },
  // 金の爪と牙 = やわらかく短い金・爪が擦れる
  claw:       { steelRate: 0.94, steelGain: 0.6,  shelfDb: -9,  bellMul: 0.35, tailMul: 0.4,  coreMul: 1.0,  weightMul: 0.7, layer: 'gold',     layerGain: 1.0 },
  // 枷・鎖・車輪 = 合わない低い鉄の鳴りと鎖の粒・鋼は低く
  iron:       { steelRate: 0.72, steelGain: 0.7,  shelfDb: -11, bellMul: 0.2,  tailMul: 0.3,  coreMul: 1.2,  weightMul: 1.0, layer: 'ironRing', layerGain: 1.0 },
  // 薄衣と銀の閃き = 細い銀を高く・布の擦れ・芯は軽い
  veil:       { steelRate: 1.12, steelGain: 0.55, shelfDb: -4,  bellMul: 1.1,  tailMul: 0.9,  coreMul: 0.55, weightMul: 0.3, layer: 'silk',     layerGain: 1.0 },
  // 砲の弾・衝撃波 = 物の鳴りなし・空気の圧
  shell:      { steelRate: 0.78, steelGain: 0.1,  shelfDb: -14, bellMul: 0,    tailMul: 0,    coreMul: 0.9,  weightMul: 0.9, layer: 'air',      layerGain: 1.0 },
}

/** f の重さ: e の重さ (0..1) を 0.3〜0.85 に狭める (材質の差を主にする)。 */
export function parryMaterialVoiceWeight(w: number): number {
  return 0.3 + 0.55 * Math.max(0, Math.min(1, w))
}
